



















































import { Component, Prop, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { StoreModule } from "@/store/types";
import { OrderActions, OrderGetters } from "@/store/modules/order/types";
import { GlobalActions, GlobalGetters } from "@/store/modules/global/types";
import { Order } from "@/models/order/order";
import { PriceLevel } from "@/models/netsuite/netsuite";
import { priceLevelMultiplier } from "@/utility/helpers";
import Vue from "vue";

@Component
export default class EditMultiplier extends Vue {
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetOrder))
  storeOrder!: Order;
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetMultiplier))
  multiplier!: PriceLevel | null;
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetPriceLevels))
  storePriceLevels!: PriceLevel[];
  @(namespace(StoreModule.Global).Action(GlobalActions.AddMultiplier))
  addMultiplier!: (value: PriceLevel | null) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.ApplyQuoteMultiplier))
  applyQuoteMultiplier!: (value: number) => Promise<void>;

  @Prop({ default: "btn btn-sm border border-white text-white bg-brand-700 hover:bg-brand-600" })
  buttonClass!: string;

  @Prop({ default: "" })
  wrapClass!: string;

  @Prop({ default: false })
  disabled!: boolean;

  protected selectedPriceLevelId: number | null = null;
  protected showModal = false;
  protected saving = false;

  @Watch("multiplier")
  private onStoreMultiplierChange(level: PriceLevel | null): void {
    // Never while the modal is open: hydration elsewhere (the cart resolves the
    // customer's level on mount) would otherwise overwrite a staged selection
    // out from under the rep.
    if (this.showModal) return;
    this.selectedPriceLevelId = level?.id ?? null;
  }

  async created() {
    const isValidMultiplier =
      this.multiplier !== null &&
      typeof this.multiplier === "object" &&
      typeof (this.multiplier as any).id === "number";
    if (!isValidMultiplier && this.multiplier !== null) {
      await this.addMultiplier(null);
    }
    if (isValidMultiplier) {
      this.selectedPriceLevelId = (this.multiplier as PriceLevel).id;
    } else if (this.storeOrder?.customer_netsuite_price_level_id) {
      const match = this.storePriceLevels.find(
        l => l.id === this.storeOrder.customer_netsuite_price_level_id
      );
      if (match) {
        this.selectedPriceLevelId = match.id;
        await this.addMultiplier(match);
      }
    }
  }

  protected get hasCustomerDefault(): boolean {
    return this.storeOrder?.customer_netsuite_price_level_discount != null;
  }

  protected get customerDefaultMultiplierDisplay(): string {
    const discount = this.storeOrder?.customer_netsuite_price_level_discount;
    if (discount == null) return "";
    const value = priceLevelMultiplier(discount);
    const match = this.storePriceLevels.find(
      l => l.id === this.storeOrder.customer_netsuite_price_level_id
    );
    return match ? match.name : `${value}`;
  }

  protected get multiplierValue(): number {
    if (!this.multiplier || this.multiplier.discountpct == null) return 1;
    return priceLevelMultiplier(this.multiplier.discountpct);
  }

  /** The level currently in force, which the staged selection is measured against. */
  protected get currentPriceLevelId(): number | null {
    return this.multiplier?.id ?? null;
  }

  private get selectedLevel(): PriceLevel | null {
    return (
      this.storePriceLevels.find(l => l.id === this.selectedPriceLevelId) ??
      null
    );
  }

  /** What Save would stamp onto every line. */
  private get selectedMultiplierValue(): number {
    const level = this.selectedLevel;
    if (!level || level.discountpct == null) return 1;
    return priceLevelMultiplier(level.discountpct);
  }

  /**
   * Whether saving would change anything — not merely whether the dropdown
   * moved. A quote whose lines took a NetSuite multiplier sits on a mix while
   * the global level is unchanged, and re-picking that same level is exactly
   * how the rep forces every line back onto it. Comparing ids alone would
   * disable Save on the one case that needs it.
   */
  protected get hasPendingChange(): boolean {
    if (this.selectedPriceLevelId === null) return false;
    if (this.selectedPriceLevelId !== this.currentPriceLevelId) return true;
    const value = this.selectedMultiplierValue;
    return (this.storeOrder?.items ?? []).some(
      item =>
        item.ns_multiplier ||
        // Rounded: these are floats, and 1 + -0.63 is not exactly 0.37.
        (item.multiplier ?? 1).toFixed(4) !== value.toFixed(4)
    );
  }

  /** Opens on the level in force, discarding any selection a Cancel left behind. */
  protected openModal(): void {
    this.selectedPriceLevelId = this.currentPriceLevelId;
    this.showModal = true;
  }

  protected cancel(): void {
    this.selectedPriceLevelId = this.currentPriceLevelId;
    this.showModal = false;
  }

  /**
   * The rep picking a level outright — nothing reaches the store or the cart
   * until this runs, and it is the only path that should release lines pinned
   * to a NetSuite multiplier. `created()` also calls `addMultiplier`, but that
   * is hydration, not a choice, and must leave the pins alone.
   */
  protected async save(): Promise<void> {
    const level = this.selectedLevel;
    if (!level) return;
    this.saving = true;
    try {
      await this.addMultiplier(level);
      // Written to the lines here, not left to the cart: this modal is also
      // shown on checkout, where nothing else re-stamps them.
      await this.applyQuoteMultiplier(this.selectedMultiplierValue);
      this.$emit("change", level);
      this.showModal = false;
    } finally {
      this.saving = false;
    }
  }
}
